export type Success<R> = { success: true; value: R };
export type Failure<I> = { success: false; error: unknown; item: I };
export type Result<R, I> = Success<R> | Failure<I>;

export function success<T>(value: T): Success<T> {
  return { success: true, value };
}

export function failure<I>(error: unknown, item: I): Failure<I> {
  return { success: false, error, item };
}

interface RunConcurrentOptions {
  concurrency: number;
  retries?: number;
  retryBaseDelayMs?: number;
  onProgress?: (completed: number, total: number) => void;
}

export async function runConcurrent<I, R>(
  items: I[],
  fn: (item: I) => Promise<R>,
  options: RunConcurrentOptions,
): Promise<Result<R, I>[]> {
  const {
    concurrency,
    retries = 0,
    retryBaseDelayMs = 1000,
    onProgress,
  } = options;

  if (concurrency <= 0) {
    throw new Error("Concurrency must be greater than 0");
  }

  const results: Result<R, I>[] = new Array(items.length);
  let currentIndex = 0;
  let completedCount = 0;

  async function runWithRetry(item: I): Promise<R> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fn(item);
      } catch (error) {
        if (attempt === retries) throw error;
        await new Promise((r) =>
          setTimeout(r, retryBaseDelayMs * 2 ** attempt),
        );
      }
    }
    throw new Error("Unreachable");
  }

  async function worker() {
    while (currentIndex < items.length) {
      const index = currentIndex++;
      const item = items[index]!;

      try {
        results[index] = success(await runWithRetry(item));
      } catch (error) {
        results[index] = failure(error, item);
      }

      completedCount++;
      onProgress?.(completedCount, items.length);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker(),
  );
  await Promise.all(workers);

  return results;
}
